import { useState, useEffect } from 'react';
import { Link, useOutletContext } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import { publicAPI, getMediaBySlugUrl } from '@/lib/api';
import {
  MapPin, CheckCircle, ArrowRight, Clock, Car,
  Plane, Train, MousePointerClick, CalendarCheck, ShieldCheck,
  Handshake, Building2, Navigation, Star, AlertCircle
} from 'lucide-react';

const HERO_FALLBACK = process.env.REACT_APP_HERO_FALLBACK_IMAGE || '';

const HeroSection = ({ content, media, activeHero }) => {
  const h = activeHero || {};
  const hasHero = !!activeHero?.id;

  // Alle Texte kommen IMMER aus dem CMS-Content (Inhalte-Editor)
  // Hero-Kombinationen steuern NUR das Bild
  const heroTitle = content?.hero_ueberschrift || 'Mietwagen auf Sylt';
  const heroSub = content?.hero_unterzeile || 'Flexible Mobilität auf Deutschlands schönster Insel';
  const heroBtn = content?.hero_button || 'Jetzt Mietwagen buchen';
  const heroHint = content?.hero_hinweistext || '';
  const heroPrice = content?.hero_teaserpreis || '';
  const heroPriceHint = content?.hero_preishinweis || '';
  const heroPickup = content?.hero_uebernahmehinweis || '';
  const heroKoop = content?.kooperation_hinweis || 'Ein Service von GB Sylt in Kooperation mit unserem Mietwagenpartner.';

  let bgImage = HERO_FALLBACK;
  if (hasHero && h.image_url) {
    bgImage = h.image_url.startsWith('/') ? `${process.env.REACT_APP_BACKEND_URL}${h.image_url}` : h.image_url;
  } else {
    const heroMedia = media.find(m => m.slug === 'hero_image' && m.active);
    if (heroMedia) bgImage = getMediaBySlugUrl('hero_image');
  }

  return (
    <section data-testid="hero-section" className="bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-16 items-center py-12 md:py-20 lg:py-24">
          <div className="order-2 lg:order-1">
            <div className="inline-flex items-center gap-2 bg-[#0E518D]/8 text-[#0E518D] px-4 py-2 rounded-full text-sm font-body font-medium mb-6 opacity-0 animate-fade-in-up">
              <Handshake className="w-4 h-4" strokeWidth={1.5} />
              {heroKoop}
            </div>

            <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold text-slate-900 leading-[1.1] mb-5 opacity-0 animate-fade-in-up animate-delay-100" data-testid="hero-heading">
              {heroTitle}
            </h1>

            <p className="text-base md:text-lg text-slate-600 font-body leading-relaxed mb-8 max-w-xl opacity-0 animate-fade-in-up animate-delay-200" data-testid="hero-subtitle">
              {heroSub}
            </p>

            <div className="flex flex-col sm:flex-row items-start gap-4 mb-6 opacity-0 animate-fade-in-up animate-delay-300">
              <Link to="/go" data-testid="hero-cta-button">
                <Button className="bg-[#0E518D] hover:bg-[#0B4171] text-white rounded-sm px-8 py-4 font-body font-medium tracking-wide text-sm uppercase shadow-md hover:shadow-lg transition-all duration-300">
                  {heroBtn}
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </Link>
              {heroPrice && (
                <div className="flex items-center gap-2 px-4 py-2.5 border border-[#D4AF37]/30 bg-[#D4AF37]/5 rounded-sm" data-testid="hero-price-badge">
                  <span className="text-[#0E518D] font-body font-bold text-sm">{heroPrice}</span>
                  {heroPriceHint && (
                    <span className="text-slate-500 text-xs font-body">{heroPriceHint}</span>
                  )}
                </div>
              )}
            </div>

            {heroPickup && (
              <p className="text-slate-500 text-sm font-body flex items-center gap-2 opacity-0 animate-fade-in-up animate-delay-400" data-testid="hero-pickup-hint">
                <MapPin className="w-4 h-4 text-[#0E518D] flex-shrink-0" strokeWidth={1.5} />
                {heroPickup}
              </p>
            )}

            {heroHint && (
              <p className="text-slate-500 text-xs font-body mt-3 italic opacity-0 animate-fade-in-up animate-delay-400" data-testid="hero-hint-text">
                {heroHint}
              </p>
            )}
          </div>

          <div className="order-1 lg:order-2 opacity-0 animate-fade-in-up animate-delay-200">
            <div className="relative">
              <div className="rounded-sm overflow-hidden shadow-xl">
                <img
                  src={bgImage}
                  alt="Mietwagen auf Sylt"
                  className="w-full h-64 sm:h-80 lg:h-[420px] object-cover"
                  data-testid="hero-image"
                />
              </div>
              <div className="absolute -bottom-4 -left-4 w-24 h-24 bg-[#0E518D]/10 rounded-sm -z-10" />
              <div className="absolute -top-4 -right-4 w-16 h-16 bg-[#D4AF37]/10 rounded-sm -z-10" />
            </div>
          </div>
        </div>
      </div>

      <div className="bg-[#0E518D]/5 border-y border-[#0E518D]/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex flex-col sm:flex-row items-center justify-center gap-3 text-center">
            <Handshake className="w-5 h-5 text-[#0E518D]" strokeWidth={1.5} />
            <p className="text-sm font-body text-slate-700" data-testid="cooperation-banner">
              {content?.weiterleitung_hinweis || 'Die Buchung erfolgt auf der externen Seite unseres Partners. Sie werden automatisch weitergeleitet.'}
            </p>
          </div>
        </div>
      </div>
    </section>
  );
};

const TrustBanner = ({ content }) => {
  const text = content?.trust_hinweis;
  if (!text) return null;
  return (
    <section className="bg-[#0E518D] py-3" data-testid="trust-banner">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-center gap-2.5">
          <Star className="w-4 h-4 text-[#D4AF37] flex-shrink-0" fill="#D4AF37" strokeWidth={0} />
          <p className="text-sm font-body font-medium text-white tracking-wide">{text}</p>
          <Star className="w-4 h-4 text-[#D4AF37] flex-shrink-0" fill="#D4AF37" strokeWidth={0} />
        </div>
      </div>
    </section>
  );
};

const QuickLocations = ({ content }) => {
  const items = [
    { name: content?.standort1 || 'Bahnhof Westerland', icon: Train },
    { name: content?.standort2 || 'Flughafen Sylt', icon: Plane },
    { name: content?.standort3 || 'Kampen', icon: Building2 },
  ];
  return (
    <section className="py-8 bg-white" data-testid="quick-locations">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        <h3 className="text-center font-heading text-lg font-semibold text-slate-900 mb-5">
          {content?.schnell_standorte_titel || 'Uebernahmeorte auf Sylt'}
        </h3>
        <div className="grid grid-cols-3 gap-3 sm:gap-4">
          {items.map((item, i) => (
            <div key={i} className="flex flex-col items-center p-3 sm:p-4 bg-slate-50 rounded-sm border border-slate-100 hover:border-[#0E518D]/20 transition-colors" data-testid={`quick-location-${i}`}>
              <div className="w-10 h-10 bg-[#0E518D]/8 rounded-full flex items-center justify-center mb-2">
                <item.icon className="w-5 h-5 text-[#0E518D]" strokeWidth={1.5} />
              </div>
              <span className="text-xs sm:text-sm font-body font-medium text-slate-700 text-center">{item.name}</span>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

const MobilitySection = ({ content }) => (
  <section data-testid="mobility-section" className="py-20 md:py-28 bg-white">
    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
      <div className="w-14 h-14 bg-[#0E518D]/8 rounded-full flex items-center justify-center mx-auto mb-6">
        <Car className="w-7 h-7 text-[#0E518D]" strokeWidth={1.5} />
      </div>
      <h2 className="font-heading text-3xl md:text-4xl font-semibold text-slate-900 mb-6">
        {content?.mobilitaet_titel || 'Mobil auf Sylt unterwegs'}
      </h2>
      <p className="text-base md:text-lg text-slate-600 font-body leading-relaxed max-w-3xl mx-auto">
        {content?.mobilitaet_text || ''}
      </p>
    </div>
  </section>
);

const LocationsSection = ({ content }) => {
  const locations = [
    {
      name: content?.standort1 || 'Bahnhof Westerland',
      address: content?.standort1_adresse || 'Trift 1 im Bahnhofsgebäude, 25980 Sylt',
      icon: Train,
      detail: 'Sylt-Travel Store'
    },
    {
      name: content?.standort2 || 'Flughafen Sylt',
      address: content?.standort2_adresse || 'Flughafenstrasse 1, 25980 Sylt',
      icon: Plane,
      detail: 'Direkt am Terminal'
    },
    {
      name: content?.standort3 || 'Kampen',
      address: content?.standort3_adresse || 'Süderweg 1a, 25999 Kampen',
      icon: Building2,
      detail: 'A&M Kampen'
    },
  ];

  return (
    <section data-testid="locations-section" className="py-20 md:py-28 bg-[#F7F9FC]">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-14">
          <h2 className="font-heading text-3xl md:text-4xl font-semibold text-slate-900 mb-4">
            {content?.uebernahmeorte_titel || 'Mietwagen direkt auf Sylt übernehmen'}
          </h2>
          <p className="text-base md:text-lg text-slate-600 font-body max-w-2xl mx-auto">
            {content?.uebernahmeorte_einleitung || 'Holen Sie Ihren Mietwagen bequem an einer der folgenden Stationen ab:'}
          </p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 lg:gap-8">
          {locations.map((loc, i) => {
            const Icon = loc.icon;
            return (
              <Card key={i} className="bg-white border border-slate-100 shadow-sm hover:shadow-lg hover:-translate-y-1 transition-all duration-300 rounded-sm overflow-hidden" data-testid={`location-card-${i}`}>
                <div className="h-1.5 bg-[#0E518D]" />
                <CardContent className="p-7">
                  <div className="w-14 h-14 bg-[#0E518D]/8 rounded-full flex items-center justify-center mb-5">
                    <Icon className="w-7 h-7 text-[#0E518D]" strokeWidth={1.5} />
                  </div>
                  <h3 className="font-heading text-xl font-semibold text-slate-900 mb-1">
                    {loc.name}
                  </h3>
                  {loc.detail && (
                    <p className="text-sm font-body text-[#0E518D] font-medium mb-3">
                      {loc.detail}
                    </p>
                  )}
                  <div className="flex items-start gap-2 text-slate-500">
                    <Navigation className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" strokeWidth={1.5} />
                    <p className="text-sm font-body leading-relaxed">
                      {loc.address}
                    </p>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
        {content?.standort4 && (
          <p className="text-center text-sm text-slate-500 font-body mt-8">
            {content.standort4}
          </p>
        )}
      </div>
    </section>
  );
};

const AdvantagesSection = ({ content }) => {
  const advantages = [
    { title: content?.vorteil1_titel, text: content?.vorteil1_text, icon: CheckCircle },
    { title: content?.vorteil2_titel, text: content?.vorteil2_text, icon: Car },
    { title: content?.vorteil3_titel, text: content?.vorteil3_text, icon: ShieldCheck },
    { title: content?.vorteil4_titel, text: content?.vorteil4_text, icon: Clock },
  ];

  return (
    <section data-testid="advantages-section" className="py-20 md:py-28 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <h2 className="font-heading text-3xl md:text-4xl font-semibold text-slate-900 text-center mb-14">
          {content?.vorteile_titel || 'Ihre Vorteile'}
        </h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 lg:gap-8">
          {advantages.map((adv, i) => {
            const Icon = adv.icon;
            return (
              <div key={i} className="group" data-testid={`advantage-card-${i}`}>
                <div className="border border-slate-100 rounded-sm p-7 h-full hover:border-[#0E518D]/20 hover:shadow-md transition-all duration-300 bg-white">
                  <div className="w-11 h-11 bg-[#0E518D] rounded-sm flex items-center justify-center mb-5 group-hover:scale-105 transition-transform duration-300">
                    <Icon className="w-5 h-5 text-white" strokeWidth={1.5} />
                  </div>
                  <h3 className="font-heading text-lg font-semibold text-slate-900 mb-2">
                    {adv.title || ''}
                  </h3>
                  <p className="text-sm text-slate-600 font-body leading-relaxed">
                    {adv.text || ''}
                  </p>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

const IdealSection = ({ content }) => (
  <section data-testid="ideal-section" className="py-20 md:py-28 bg-[#F7F9FC]">
    <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-12 items-center">
        <div>
          <div className="flex gap-3 mb-6">
            <div className="w-12 h-12 bg-[#0E518D]/10 rounded-full flex items-center justify-center">
              <Train className="w-6 h-6 text-[#0E518D]" strokeWidth={1.5} />
            </div>
            <div className="w-12 h-12 bg-[#0E518D]/10 rounded-full flex items-center justify-center">
              <Plane className="w-6 h-6 text-[#0E518D]" strokeWidth={1.5} />
            </div>
          </div>
          <h2 className="font-heading text-3xl md:text-4xl font-semibold text-slate-900 mb-5">
            {content?.ideal_titel || 'Ideal bei Anreise mit Bahn oder Flugzeug'}
          </h2>
          <p className="text-base text-slate-600 font-body leading-relaxed">
            {content?.ideal_text || ''}
          </p>
        </div>
        <div className="flex justify-center">
          <div className="bg-white border border-slate-100 rounded-sm p-8 shadow-sm max-w-sm w-full">
            <div className="space-y-4">
              <div className="flex items-center gap-3 pb-4 border-b border-slate-100">
                <Train className="w-5 h-5 text-[#0E518D]" strokeWidth={1.5} />
                <div>
                  <p className="font-body font-medium text-slate-900 text-sm">Bahnhof Westerland</p>
                  <p className="text-xs text-slate-500 font-body">Direkte Übernahme im Bahnhof</p>
                </div>
              </div>
              <div className="flex items-center gap-3 pb-4 border-b border-slate-100">
                <Plane className="w-5 h-5 text-[#0E518D]" strokeWidth={1.5} />
                <div>
                  <p className="font-body font-medium text-slate-900 text-sm">Flughafen Sylt</p>
                  <p className="text-xs text-slate-500 font-body">Direkt am Terminal</p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <MapPin className="w-5 h-5 text-[#0E518D]" strokeWidth={1.5} />
                <div>
                  <p className="font-body font-medium text-slate-900 text-sm">Kampen</p>
                  <p className="text-xs text-slate-500 font-body">A&M Kampen</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </section>
);

const StepsSection = ({ content }) => {
  const steps = [
    { num: '01', title: content?.schritt1_titel, text: content?.schritt1_text, icon: MousePointerClick },
    { num: '02', title: content?.schritt2_titel, text: content?.schritt2_text, icon: ArrowRight },
    { num: '03', title: content?.schritt3_titel, text: content?.schritt3_text, icon: CalendarCheck },
  ];

  return (
    <section data-testid="steps-section" className="py-20 md:py-28 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <h2 className="font-heading text-3xl md:text-4xl font-semibold text-slate-900 text-center mb-14">
          {content?.ablauf_titel || 'So funktioniert es'}
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 lg:gap-16">
          {steps.map((step, i) => {
            const Icon = step.icon;
            return (
              <div key={i} className="relative text-center" data-testid={`step-${i}`}>
                <div className="relative inline-flex items-center justify-center mb-6">
                  <div className="w-16 h-16 bg-[#0E518D] rounded-full flex items-center justify-center">
                    <Icon className="w-7 h-7 text-white" strokeWidth={1.5} />
                  </div>
                  <span className="absolute -top-2 -right-2 w-7 h-7 bg-[#D4AF37] rounded-full text-white text-xs font-body font-bold flex items-center justify-center shadow-sm">
                    {step.num}
                  </span>
                </div>
                <h3 className="font-heading text-xl font-semibold text-slate-900 mb-3">
                  {step.title || ''}
                </h3>
                <p className="text-sm text-slate-600 font-body leading-relaxed max-w-xs mx-auto">
                  {step.text || ''}
                </p>
                {i < steps.length - 1 && (
                  <div className="hidden md:block absolute top-8 left-[calc(50%+48px)] w-[calc(100%-96px)]">
                    <div className="border-t-2 border-dashed border-[#0E518D]/15" />
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

const FAQSection = ({ content }) => {
  const faqs = [
    { q: content?.faq1_frage, a: content?.faq1_antwort },
    { q: content?.faq2_frage, a: content?.faq2_antwort },
    { q: content?.faq3_frage, a: content?.faq3_antwort },
    { q: content?.faq4_frage, a: content?.faq4_antwort },
  ].filter(f => f.q);

  return (
    <section data-testid="faq-section" className="py-20 md:py-28 bg-[#F7F9FC]">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
        <h2 className="font-heading text-3xl md:text-4xl font-semibold text-slate-900 text-center mb-12">
          {content?.faq_titel || 'Häufig gestellte Fragen'}
        </h2>
        <Accordion type="single" collapsible className="space-y-3">
          {faqs.map((faq, i) => (
            <AccordionItem key={i} value={`faq-${i}`} className="bg-white border border-slate-200 rounded-sm px-6 shadow-sm" data-testid={`faq-item-${i}`}>
              <AccordionTrigger className="font-body font-medium text-slate-800 text-left py-5 hover:no-underline text-sm">
                {faq.q}
              </AccordionTrigger>
              <AccordionContent className="text-slate-600 font-body text-sm leading-relaxed pb-5">
                {faq.a}
              </AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </div>
    </section>
  );
};

const CTASection = ({ content }) => (
  <section data-testid="cta-section" className="py-20 md:py-28 bg-white">
    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
      <div className="bg-[#0E518D] rounded-sm p-10 md:p-14 shadow-lg">
        <h2 className="font-heading text-2xl md:text-3xl font-semibold text-white mb-4">
          {content?.cta_titel || 'Jetzt Mobilität für Ihren Sylt-Aufenthalt planen'}
        </h2>
        <p className="text-base text-white/80 font-body leading-relaxed mb-6 max-w-2xl mx-auto">
          {content?.cta_text || ''}
        </p>
        {content?.verfuegbarkeits_hinweis && (
          <div className="flex items-center justify-center gap-2 mb-6" data-testid="availability-hint">
            <AlertCircle className="w-4 h-4 text-[#D4AF37] flex-shrink-0" strokeWidth={1.5} />
            <p className="text-sm text-white/70 font-body italic">{content.verfuegbarkeits_hinweis}</p>
          </div>
        )}
        <Link to="/go" data-testid="cta-button">
          <Button className="bg-white hover:bg-slate-50 text-[#0E518D] rounded-sm px-10 py-4 font-body font-semibold tracking-wide text-sm uppercase shadow-md hover:shadow-lg transition-all duration-300">
            {content?.cta_button || 'Jetzt Mietwagen buchen'}
            <ArrowRight className="w-4 h-4 ml-2" />
          </Button>
        </Link>
      </div>
    </div>
  </section>
);

const StructuredData = ({ content }) => {
  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": [1, 2, 3, 4].map(i => ({
      "@type": "Question",
      "name": content?.[`faq${i}_frage`] || '',
      "acceptedAnswer": { "@type": "Answer", "text": content?.[`faq${i}_antwort`] || '' }
    })).filter(q => q.name)
  };

  const orgSchema = {
    "@context": "https://schema.org",
    "@type": "Organization",
    "name": "GB Sylt",
    "url": "https://www.gb-sylt.de"
  };

  const webSchema = {
    "@context": "https://schema.org",
    "@type": "WebSite",
    "name": "Mietwagen GB Sylt",
    "url": "https://mietwagen.gb-sylt.de"
  };

  return (
    <>
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(faqSchema) }} />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(orgSchema) }} />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(webSchema) }} />
    </>
  );
};

export default function LandingPage() {
  const { content: layoutContent, media: layoutMedia, activeHero } = useOutletContext();
  const [content, setContent] = useState(layoutContent || {});
  const [media, setMedia] = useState(layoutMedia || []);

  useEffect(() => {
    if (layoutContent) setContent(layoutContent);
    if (layoutMedia) setMedia(layoutMedia);
  }, [layoutContent, layoutMedia]);

  useEffect(() => {
    publicAPI.getSEO().then(res => {
      const d = res.data;
      if (d.seo_title) document.title = d.seo_title;
      const setMeta = (sel, val) => { const el = document.querySelector(sel); if (el && val) el.content = val; };
      setMeta('meta[name="description"]', d.meta_description);
      setMeta('meta[name="keywords"]', d.seo_keywords);
      setMeta('meta[property="og:title"]', d.og_title);
      setMeta('meta[property="og:description"]', d.og_description);
      setMeta('meta[property="og:image"]', d.og_image);
      setMeta('meta[property="og:url"]', d.canonical_url);
      setMeta('meta[name="twitter:title"]', d.og_title);
      setMeta('meta[name="twitter:description"]', d.og_description);
      if (d.og_image) setMeta('meta[name="twitter:image"]', d.og_image);
      const canonical = document.querySelector('link[rel="canonical"]');
      if (canonical && d.canonical_url) canonical.href = d.canonical_url;
    }).catch(() => {});
  }, []);

  return (
    <div data-testid="landing-page">
      <StructuredData content={content} />
      <HeroSection content={content} media={media} activeHero={activeHero} />
      <TrustBanner content={content} />
      <QuickLocations content={content} />
      <MobilitySection content={content} />
      <LocationsSection content={content} />
      <AdvantagesSection content={content} />
      <IdealSection content={content} />
      <StepsSection content={content} />
      <FAQSection content={content} />
      <CTASection content={content} />
    </div>
  );
}
